import React from 'react';
import Tooltip from '../../Tooltip';
import Cell from './Cell';
import Row from '../../Common/Row';
import Delta from '../../Gadget/Delta';
import DeltaTooltip from '../../Gadget/DeltaTooltip';

import getComparison from './getComparison';
import useValueFormatters from '../../../hooks/useValueFormatters';
import useObfuscator from '../../../hooks/useObfuscator';
import type { IRowNode } from 'ag-grid-community';

import computeScoreColor from 'components/DriverAX/utils/bonusScoreColourUtils';

const isComparison = (
  v: string | MatrixComparisonValue | boolean,
): v is MatrixComparisonValue => {
  // @ts-ignore
  return !!v && v.formatting !== undefined;
};

const NormalCellRenderer = ({
  node,
  value: v,
  colDef,
  isLeftAlign,
}: {
  node: IRowNode;
  value: string | MatrixComparisonValue | undefined | boolean | string[];
  colDef: {
    headerClass?: string | 'rightAlignedHeader';
    headerComponentParams?: {
      metric?: {
        id: string;
      };
      isComparisonCurrentValue?: boolean;
    };
    cellRendererParams?: {
      previousDateScope?: DateRangeInput;
      currentDateScope?: DateRangeInput;
      colourConfig?: FleetOps.BonusScoreColourConfig;
    };
    field?: string;
  };
  isLeftAlign?: boolean;
}) => {
  const { formatMetric } = useValueFormatters();
  const { isObfuscating, obfuscatedValue } = useObfuscator({
    value: typeof v === 'string' || Array.isArray(v) ? v : undefined,
    field:
      colDef.headerComponentParams &&
      colDef.headerComponentParams.metric &&
      colDef.headerComponentParams.metric.id === colDef.field
        ? undefined
        : colDef.headerComponentParams &&
            colDef.headerComponentParams.isComparisonCurrentValue
          ? undefined
          : colDef.field,
  });

  if (v === undefined || v === null) {
    return (
      <Cell
        rightAlign={
          !!colDef.headerClass && colDef.headerClass === 'rightAlignedHeader'
        }
      >
        <span>-</span>
      </Cell>
    );
  }

  if (Array.isArray(v)) {
    const text = isObfuscating ? obfuscatedValue : v.join(', ');
    const shouldTooltip = text.length && text.length > 14;
    return (
      <Cell rightAlign={isLeftAlign ? false : true}>
        <Tooltip content={shouldTooltip ? text : undefined}>
          <span>{text}</span>
        </Tooltip>
      </Cell>
    );
  }

  if (isComparison(v)) {
    const {
      value,
      previous,
      formatting,
      type,
      currentDateRange,
      previousDateRange,
      metric,
    } = v;

    // TODO: HERE
    if (previous === 0 && type === 'percentDelta') {
      return (
        <Cell rightAlign={isLeftAlign ? false : true}>
          <span>-</span>
        </Cell>
      );
    }

    if (type === 'percentDelta' || type === 'delta') {
      const fallbackCurrentRange =
        currentDateRange || colDef?.cellRendererParams?.currentDateScope;
      const fallbackPreviousRange =
        previousDateRange || colDef?.cellRendererParams?.previousDateScope;

      const { delta, isGood } = getComparison({
        value: value as number,
        previous: previous as number,
        formatting,
        type,
      });
      return (
        <Row centerAlign>
          {fallbackCurrentRange && fallbackPreviousRange && (
            <Tooltip
              content={
                <DeltaTooltip
                  metric={metric}
                  current={value as number}
                  previous={previous as number}
                  currentDateRange={fallbackCurrentRange}
                  previousDateRange={fallbackPreviousRange}
                />
              }
              isAltTooltip
            >
              <Delta
                isGood={isGood}
                delta={delta}
                isGrid
                isRaw={type === 'delta'}
                metricId={metric.id}
              />
            </Tooltip>
          )}
          {(!fallbackCurrentRange || !fallbackPreviousRange) && (
            <Delta
              isGood={isGood}
              delta={delta}
              isGrid
              isRaw={type === 'delta'}
              metricId={metric.id}
            />
          )}
        </Row>
      );
    } else {
      const formattedValue = formatMetric({ metricId: metric.id, value });

      return (
        <Cell rightAlign={isLeftAlign ? false : true}>
          <span>{formattedValue}</span>
        </Cell>
      );
    }
  } else if (typeof v === 'boolean') {
    return (
      <Cell rightAlign={isLeftAlign ? false : true}>
        <span>{v.toString()}</span>
      </Cell>
    );
  } else {
    const shouldTooltip = !!v && !!v.length && v.length > 14;
    const valueToShow = isObfuscating ? obfuscatedValue : v;
    const rawValue = node.data[`${colDef.field}-raw`];
    const isDateField = (() => {
      try {
        // @ts-ignore
        return colDef.cellRendererParams.fieldType === 'date';
      } catch (e) {
        return false;
      }
    })();

    if (node.isRowPinned() && (typeof rawValue === 'string' || isDateField)) {
      return <Cell />;
    }

    // Threshold colours apply to body cells only - totals aren't comparable
    const colourConfig = colDef.cellRendererParams?.colourConfig;
    const color =
      colourConfig && !node.isRowPinned() && typeof rawValue === 'number'
        ? computeScoreColor({ value: rawValue, colourConfig })
        : undefined;

    return (
      <Cell rightAlign={isLeftAlign ? false : true}>
        <Tooltip content={shouldTooltip ? valueToShow : undefined}>
          <span style={color ? { color } : undefined}>
            {valueToShow.toString()}
          </span>
        </Tooltip>
      </Cell>
    );
  }
};

export default NormalCellRenderer;
